import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import { createAuthClient } from '../client/auth-client.js';
import { registerSessionRefresher } from '../client/session-refresh.js';
/** localStorage key used to persist the bearer token across reloads. */
export const TOKEN_STORAGE_KEY = 'auth.sessionToken';
/** localStorage key for the refresh token that renews the session when the bearer expires. */
export const REFRESH_TOKEN_STORAGE_KEY = 'auth.refreshToken';
const AuthContext = React.createContext(null);
function defaultStorage() {
    if (typeof window === 'undefined')
        return null;
    try {
        return window.localStorage;
    }
    catch {
        return null;
    }
}
/**
 * Top-level provider that owns the bearer token and the typed API client.
 *
 * Wrap the app once near the root: `<AuthProvider apiBase={env.apiUrl} />`.
 * All other hooks in this package read their dependencies from this context.
 */
export function AuthProvider({ children, apiBase, tokenStorage, onUnauthenticated, }) {
    const storage = React.useMemo(() => tokenStorage ?? defaultStorage(), [tokenStorage]);
    // Initial token is hydrated synchronously so the first render has the right
    // shape and we don't flicker an "unauthenticated" UI on reload.
    const [token, setTokenState] = React.useState(() => storage?.getItem(TOKEN_STORAGE_KEY) ?? null);
    const [user, setUser] = React.useState(null);
    // tokenRef is the source of truth the API client reads via `getToken()`. We can NOT defer
    // its update to a useEffect — child components' effects (e.g. `useAuthMe.load()` inside the
    // app's <UnauthenticatedRedirector>) fire BEFORE the parent's effects, so a deferred sync
    // would mean the post-login `/auth/me` call goes out with the OLD token still in the ref →
    // 401 → setUser(null) → bounce back to /login. The user then logs in a second time
    // (because the ref is now warm) and lands on the dashboard. We saw this as "login takes
    // two attempts."
    // Update tokenRef synchronously inside setToken instead. The ref now matches whatever was
    // last written, regardless of when React commits the state update.
    const tokenRef = React.useRef(token);
    const onUnauthenticatedRef = React.useRef(onUnauthenticated);
    React.useEffect(() => {
        onUnauthenticatedRef.current = onUnauthenticated;
    }, [onUnauthenticated]);
    // The refresh token is never rendered, so it needs no state — storage when there is one (so
    // another tab's refresh is picked up), otherwise this ref.
    const refreshTokenRef = React.useRef(storage?.getItem(REFRESH_TOKEN_STORAGE_KEY) ?? null);
    const setRefreshToken = React.useCallback((next) => {
        refreshTokenRef.current = next;
        if (storage === null)
            return;
        if (next === null || next.length === 0) {
            storage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
        }
        else {
            storage.setItem(REFRESH_TOKEN_STORAGE_KEY, next);
        }
    }, [storage]);
    const setToken = React.useCallback((next) => {
        tokenRef.current = next;
        setTokenState(next);
        // Signing out drops the refresh token too, or the next 401 would sign the user back in.
        if (next === null || next.length === 0)
            setRefreshToken(null);
        if (storage === null)
            return;
        if (next === null || next.length === 0) {
            storage.removeItem(TOKEN_STORAGE_KEY);
        }
        else {
            storage.setItem(TOKEN_STORAGE_KEY, next);
        }
    }, [storage, setRefreshToken]);
    // The client is created exactly once. Token reads always go through the ref
    // so the same client instance picks up newer tokens without being recreated
    // (which would invalidate every memoised hook in consumer apps).
    const client = React.useMemo(() => createAuthClient({
        apiBase,
        getToken: () => tokenRef.current,
        setToken,
        getRefreshToken: () => storage === null ? refreshTokenRef.current : storage.getItem(REFRESH_TOKEN_STORAGE_KEY),
        setRefreshToken,
        onUnauthenticated: () => onUnauthenticatedRef.current?.(),
    }), [apiBase, setToken, storage, setRefreshToken]);
    // Lets the SPA's own API client refresh through this client on a 401 (see `refreshSessionOnce`).
    React.useEffect(() => registerSessionRefresher((failedToken) => client.refreshSessionOnce(failedToken)), [client]);
    // If the token disappears (logout, manual storage clear, expired refresh),
    // make sure the cached user follows it so guards re-render correctly.
    React.useEffect(() => {
        if (token === null)
            setUser(null);
    }, [token]);
    // Keep multiple tabs in sync: when one tab logs in/out, the other reflects it.
    React.useEffect(() => {
        if (typeof window === 'undefined')
            return undefined;
        const handler = (event) => {
            if (event.key !== TOKEN_STORAGE_KEY)
                return;
            // localStorage.removeItem dispatches a StorageEvent with newValue === null (NOT
            // an empty string) — the previous `=== ''` check meant cross-tab logout never
            // propagated. Normalize empty strings too as belt-and-suspenders.
            const raw = event.newValue;
            const next = raw === null || raw === '' ? null : raw;
            tokenRef.current = next;
            setTokenState(next);
        };
        window.addEventListener('storage', handler);
        return () => window.removeEventListener('storage', handler);
    }, []);
    const value = React.useMemo(() => ({ client, token, setToken, user, setUser }), [client, token, setToken, user]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
/** Returns the live context value. Throws if used outside `<AuthProvider>`. */
export function useAuthContext() {
    const value = React.useContext(AuthContext);
    if (value === null) {
        throw new Error('Auth hooks must be used inside an <AuthProvider>.');
    }
    return value;
}
/** Returns the typed API client. */
export function useAuthClient() {
    return useAuthContext().client;
}
//# sourceMappingURL=auth-provider.js.map